import { DisboAccount } from './../data/DisboAccount';
import { Transaction } from './../data/Transaction';
import { TransferSummary } from './../data/TransferSummary';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { HeaderService } from './header.service';
import { AuthService } from './auth.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Output, EventEmitter } from '@angular/core';
import { VisitorCardStub } from '../data/VisitorCardStub';

const RECENT_VISITOR_CARD_STUBS = 'recent_visitorCardStubs';
const SELECTED_VISITOR_CARD_STUB = 'selected_visitorCardStub';

@Injectable({
  providedIn: 'root'
})
export class VisitorCardService {
  passedParams: any = {};
  visitorCardStub: VisitorCardStub;

  @Output() visitorCardStubSelected: EventEmitter<
    VisitorCardStub
  > = new EventEmitter();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private headerService: HeaderService
  ) {
    this.authService.loginStatusChanged.subscribe(loginStatus => {
      if (!loginStatus) {
        this.clearStoredVisitorCardStubs();
      }
    });
  }

  clearPassedParams() {
    this.passedParams = {};
  }

  getPassedParams() {
    return this.passedParams;
  }

  getPassedParam(paramName: string) {
    const returnVal = this.passedParams[paramName];
    return returnVal === null ? '' : returnVal;
  }

  setPassedParam(name: string, value: string) {
    this.passedParams[name] = value;
  }

  getVisitorCardStubs(searchString: string): Observable<VisitorCardStub[]> {
    const url = `${environment.rootApiUrl}/cardholders/visitor/search`;
    let params = new HttpParams();
    params = params.append('firstParameter', searchString || '');

    return this.http.post<VisitorCardStub[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getRecentVisitorCardStubs(): VisitorCardStub[] {
    const recentVisitorCards = window.sessionStorage.getItem(
      RECENT_VISITOR_CARD_STUBS
    );
    let parsedVisitorCards: VisitorCardStub[] = [];
    if (recentVisitorCards) {
      parsedVisitorCards = JSON.parse(recentVisitorCards);
    }
    return parsedVisitorCards;
  }

  getVisitorCardDetails(visitorCardNumber: number): Observable<any> {
    const url = `${
      environment.rootApiUrl
      }/cardholders/visitor/profile/${visitorCardNumber}`;

    return this.http.get<any>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getVisitorCardTransactions(
    cardSeq: number,
    startDate: string = null,
    endDate: string = null
  ): Observable<Transaction[]> {
    const url = `${
      environment.rootApiUrl
      }/cardholders/visitor/profile/transactions`;
    let params = new HttpParams();
    params = params.append('cardSeq', '' + cardSeq);
    params = params.append('startMonth', startDate);
    params = params.append('endMonth', endDate);

    return this.http.post<Transaction[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getC2CDetails(
    acctNum: string,
    tranSeq: string,
    tranType: string
  ): Observable<any> {
    const url = `${environment.rootApiUrl}/common/cardholders/c2cDetails`;
    let params = new HttpParams();
    params = params.append('acctNum', acctNum);
    params = params.append('tranSeq', tranSeq);
    params = params.append('tranType', tranType);

    return this.http.post<any[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  updateSelectedVisitorCardStatus(cardStatus: string): Observable<any> {
    const url = `${environment.rootApiUrl}/cardholders/visitor/profile/update`;
    let params = new HttpParams();
    params = params.append(
      'cardSeq',
      '' + this.getSelectedVisitorCardStub().cardSeq
    );
    params = params.append('cardStatus', cardStatus);

    return this.http.post<VisitorCardStub[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getDisboAccounts(): Observable<DisboAccount[]> {
    const url = `${environment.rootApiUrl}/cardholders/visitor/disbo/accounts`;

    return this.http.get<DisboAccount[]>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  balanceTransfer(
    srcAccount: string,
    desAccount: string,
    transferAmt: string,
    toggleSourceDestination: boolean
  ): Observable<TransferSummary> {
    const url = `${
      environment.rootApiUrl
      }/cardholders/visitor/profile/action/balance_xfer`;
    let params = new HttpParams();
    params = params.append('srcAcct', srcAccount);
    params = params.append('desAcct', desAccount);
    params = params.append('xferAmt', transferAmt);
    params = params.append(
      'toggleSourceDestination',
      '' + toggleSourceDestination
    );

    return this.http.post<TransferSummary>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  formatDate(dateIn: Date) {
    return (
      dateIn.getMonth() +
      1 +
      '/' +
      dateIn.getDate() +
      '/' +
      dateIn.getFullYear()
    );
  }

  clearStoredVisitorCardStubs() {
    window.sessionStorage.removeItem(RECENT_VISITOR_CARD_STUBS);
    window.sessionStorage.removeItem(SELECTED_VISITOR_CARD_STUB);
  }

  storeRecentVisitorCardStub(visitorCardStub: VisitorCardStub) {
    // Store the visitorCard if it is not already stored
    const visitorCardStubs = this.getRecentVisitorCardStubs();
    let found = false;
    visitorCardStubs.forEach(elem => {
      if (elem.cardSeq === visitorCardStub.cardSeq) {
        found = true;
      }
    });
    if (!found) {
      visitorCardStubs.push(visitorCardStub);
      window.sessionStorage.setItem(
        RECENT_VISITOR_CARD_STUBS,
        JSON.stringify(visitorCardStubs)
      );
    }
  }

  selectVisitorCardStub(visitorCardStub: VisitorCardStub) {
    window.sessionStorage.setItem(
      SELECTED_VISITOR_CARD_STUB,
      JSON.stringify(visitorCardStub)
    );
    this.storeRecentVisitorCardStub(visitorCardStub);
    this.visitorCardStubSelected.emit(visitorCardStub);
  }

  getSelectedVisitorCardStub(): VisitorCardStub {
    const selectedVisitorCardStub = window.sessionStorage.getItem(
      SELECTED_VISITOR_CARD_STUB
    );
    let parsedVisitorCardStub: VisitorCardStub = null;
    if (selectedVisitorCardStub) {
      parsedVisitorCardStub = JSON.parse(selectedVisitorCardStub);
    }
    return parsedVisitorCardStub;
  }
}
